import type { BadgeTone } from '../engine/types.ts';
import { formatMoney } from '@base/shared';
import { type PortalAccountSubscription } from '@base/shared/modules/factory';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useReadOnly } from './read-only.ts';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api.ts';
import { ConfirmDialog } from '../../../components/Modal.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Loading,
  Panel,
  Table,
} from '../../../components/ui.tsx';

const STATUS_TONE: Record<PortalAccountSubscription['status'], BadgeTone> = {
  awaiting_payment: 'neutral',
  active: 'ok',
  past_due: 'off',
  paused: 'off',
  cancelled: 'off',
};

/** Data curta no idioma da tela (ex.: 30/11/2026). */
export function useDay() {
  const { i18n } = useTranslation();
  return (iso: string) => new Date(iso).toLocaleDateString(i18n.language);
}

/** Pagar a assinatura (por enquanto, o pagamento de teste). */
export function usePay(accountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/portal/subscriptions/${accountId}/simulate-payment`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal'] }),
  });
}

/**
 * A assinatura em Minha conta (área do cliente, ADR 0211): o plano, a situação, até quando está
 * pago, os recibos e o cancelamento. Aparece só para quem é cliente.
 */
export function SubscriptionSection() {
  const account = useQuery({
    queryKey: ['portal', 'account'],
    queryFn: () => api.get<PortalAccountSubscription[]>('/portal/account'),
  });
  if (account.isPending) return <Loading />;
  if (account.isError) return <ErrorBox error={account.error} />;
  return <SubscriptionList subs={account.data} />;
}

/** As assinaturas já carregadas (também em "Ver como o cliente", ADR 0223). */
export function SubscriptionList({ subs }: { subs: PortalAccountSubscription[] }) {
  return (
    <>
      {subs.map((s) => (
        <SubscriptionPanel key={s.accountId} sub={s} many={subs.length > 1} />
      ))}
    </>
  );
}

function SubscriptionPanel({ sub, many }: { sub: PortalAccountSubscription; many: boolean }) {
  const { t, i18n } = useTranslation();
  const day = useDay();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const pay = usePay(sub.accountId);
  const readOnly = useReadOnly();
  const cancel = useMutation({
    mutationFn: () => api.post(`/portal/subscriptions/${sub.accountId}/cancel`),
    onSuccess: async () => {
      setConfirm(false);
      await queryClient.invalidateQueries({ queryKey: ['portal'] });
    },
  });
  const money = (amount: string, currency: string) =>
    formatMoney({ amount, currency }, i18n.language);
  return (
    <Panel
      title={
        many
          ? t('factory.subscription.titleOf', { name: sub.name })
          : t('factory.subscription.title')
      }
      action={
        <Badge tone={STATUS_TONE[sub.status]}>
          {t(`factory.subscription.status.${sub.status}`)}
        </Badge>
      }
    >
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        <dt className="text-muted">{t('factory.subscription.plan')}</dt>
        <dd>
          {t('factory.subscription.planValue', {
            plan: t(`factory.subscription.plans.${sub.planCode}`, { defaultValue: sub.planCode }),
            price: money(sub.amount, sub.currency),
          })}
        </dd>
        {sub.paidUntil && sub.status !== 'cancelled' && (
          <>
            <dt className="text-muted">{t('factory.subscription.paidUntil')}</dt>
            <dd>{day(sub.paidUntil)}</dd>
          </>
        )}
      </dl>
      {sub.pausesAt && (
        <p className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
          {t('factory.subscription.lateNote', { date: day(sub.pausesAt) })}
        </p>
      )}
      {sub.status === 'paused' && (
        <p className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger">
          {t('factory.subscription.pausedNote')}
        </p>
      )}
      {sub.canPay && sub.simulatedPayment && !readOnly && (
        <div className="flex flex-col items-start gap-2">
          <Button onClick={() => pay.mutate()} disabled={pay.isPending}>
            {pay.isPending
              ? t('factory.portal.payment.paying')
              : t('factory.portal.payment.simulate')}
          </Button>
          <span className="text-xs text-muted">{t('factory.portal.payment.simulatedNote')}</span>
          <ErrorBox error={pay.error} />
        </div>
      )}

      <h3 className="mt-2 text-sm font-semibold">{t('factory.subscription.receipts')}</h3>
      <Table
        head={[
          t('factory.subscription.paidAt'),
          t('factory.subscription.period'),
          { label: t('factory.subscription.amount'), align: 'right' },
        ]}
      >
        {sub.payments.length === 0 && (
          <EmptyRow colSpan={3}>{t('factory.subscription.noReceipts')}</EmptyRow>
        )}
        {sub.payments.map((p) => (
          <tr key={p.id}>
            <td className="tabular-nums">{day(p.paidAt)}</td>
            <td className="tabular-nums">
              {t('factory.subscription.periodValue', {
                from: day(p.periodStart),
                to: day(p.periodEnd),
              })}
              {p.provider === 'simulated' && (
                <span className="text-xs text-muted"> · {t('factory.subscription.test')}</span>
              )}
            </td>
            <td className="text-right tabular-nums">{money(p.amount, p.currency)}</td>
          </tr>
        ))}
      </Table>

      {sub.status !== 'cancelled' && !readOnly && (
        <div className="flex flex-col items-start gap-1 border-t border-rule pt-4">
          <Button variant="secondary" onClick={() => setConfirm(true)}>
            {t('factory.subscription.cancel')}
          </Button>
          <span className="text-xs text-muted">{t('factory.subscription.cancelHint')}</span>
        </div>
      )}
      <ConfirmDialog
        open={confirm}
        title={t('factory.subscription.confirm.title')}
        description={t('factory.subscription.confirm.text')}
        confirmLabel={t('factory.subscription.confirm.action')}
        cancelLabel={t('factory.subscription.confirm.keep')}
        tone="danger"
        busy={cancel.isPending}
        error={cancel.error}
        onConfirm={() => cancel.mutate()}
        onCancel={() => setConfirm(false)}
      />
    </Panel>
  );
}
